/**
 * Přihlášený uživatel + role.
 *
 * Role řídí viditelnost akcí (spec 08 §Stack): spouštění jobů a editace jsou
 * od role `analyst` výš, konfigurace AI je `admin`. Backend to stejně vynucuje
 * (require_role v core/auth.py) — UI to jen nenabízí, aby uživatel nenarazil
 * do 403.
 */
import { useMemo } from 'react';
import { useCurrentUser } from '@/api/queries';
import type { Role } from '@/api/models';

const ROLE_ORDER: Record<Role, number> = { viewer: 0, analyst: 1, admin: 2 };

export function useAuth() {
  const { data: user, isLoading, isError, error } = useCurrentUser();

  return useMemo(() => {
    const role = user?.role ?? null;
    const atLeast = (minimum: Role) =>
      role !== null && ROLE_ORDER[role] >= ROLE_ORDER[minimum];

    return {
      user: user ?? null,
      role,
      isLoading,
      isError,
      error,
      isAuthenticated: Boolean(user),
      atLeast,
      canEdit: atLeast('analyst'),
      canRunJobs: atLeast('analyst'),
      isAdmin: atLeast('admin'),
    };
  }, [user, isLoading, isError, error]);
}
